import { LOCATION_TYPE_LABELS } from '@/constants/app/locations';
import type {
  LocationDetail,
  LocationInstalledMachines,
  LocationSaleTrends,
} from '@/types/location';
import type { Columns, TableToolbarFilterConfig } from '@bitstopco/bitstop-theme';
import { CONSTANTS, ClickableText } from '@bitstopco/bitstop-theme';
import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

export const formatCoordinate = (value?: number | null) => {
  const coordinate = Number(value);
  return value == null || !Number.isFinite(coordinate)
    ? '-'
    : String(Number(coordinate.toFixed(7)));
};

export const LOCATION_DETAILS: Columns<LocationDetail> = {
  defaultValue: '-',
  items: [
    {
      label: 'Name',
      value: 'location_name',
      valueSx: CONSTANTS.TWO_LINES_ELLIPSIS,
    },
    {
      label: 'Location ID',
      value: 'location_id',
      valueMapper: (value: LocationDetail['location_id']) =>
        value && (
          <ClickableText
            fixIcon
            text={value.toString()}
            clipboardValue={value.toString()}
            name="Location ID"
            sx={{
              '& span': {
                whiteSpace: 'normal',
              },
            }}
          />
        ),
    },
    {
      label: 'Address',
      value: 'full_address',
      valueSx: CONSTANTS.TWO_LINES_ELLIPSIS,
    },
    {
      label: 'Latitude',
      value: 'latitude',
      valueMapper: (value: LocationDetail['latitude']) => formatCoordinate(value),
    },
    {
      label: 'Longitude',
      value: 'longitude',
      valueMapper: (value: LocationDetail['longitude']) => formatCoordinate(value),
    },
    {
      label: 'Type',
      value: 'type',
      valueMapper: (value: LocationDetail['type']) => {
        if (!value) return '-';
        return (
          <Chip
            size="small"
            label={LOCATION_TYPE_LABELS[value]}
            color={(value === 'native' ? 'primary' : 'default') as ChipProps['color']}
            component="span"
          />
        );
      },
    },
    {
      label: 'Platform',
      value: 'platforms_display',
    },
    {
      label: 'Merchant',
      value: 'merchant_details',
    },
    {
      label: 'Created At (UTC)',
      value: 'created_at',
      valueMapper: (value: LocationDetail['created_at']) =>
        value ? new Date(value).toUTCString() : '-',
    },
    {
      label: 'Created At (Local Date)',
      value: 'created_at',
      valueMapper: (value: LocationDetail['created_at']) =>
        value ? new Date(value).toLocaleString() : '-',
    },
    {
      label: 'Hours',
      value: 'hours',
      valueMapper: (value: LocationDetail['hours']) =>
        value && (
          <div
            dangerouslySetInnerHTML={{
              __html: (Array.isArray(value) ? value : ['-']).join('<br/>'),
            }}
          />
        ),
    },
    {
      label: 'Vault branch',
      value: 'vault_branch',
    },
    {
      label: 'Service branch',
      value: 'service_branch',
    },
    {
      label: 'Profiles completed',
      value: 'profile_completed',
      valueMapper: (value: LocationDetail['profile_completed']) =>
        value == null ? '-' : value ? 'Yes' : 'No',
    },
  ],
};

export const OTC_LOCATION_DETAILS_ITEMS: Columns<LocationDetail>['items'] = [
  {
    label: 'OTC-Platform',
    value: 'otc_platform',
  },
  {
    label: 'OTC-Merchant',
    value: 'otc_merchant',
    valueSx: CONSTANTS.TWO_LINES_ELLIPSIS,
  },
  {
    label: 'OTC-LocationID',
    value: 'otc_location_id',
  },
];

export const getOtcDetails = (location: LocationDetail) => {
  const platforms = location.platforms ?? [];
  return {
    otc_platform: platforms.map(({ platform_key }) => platform_key).join(', ') || '-',
    otc_merchant: location.merchant?.company_name || location.merchant?.id || '-',
    otc_location_id:
      platforms.map(({ platform_location_id }) => platform_location_id).join(', ') || '-',
  };
};

export const getLocationDetailsColumns = (
  type?: LocationDetail['type'],
): Columns<LocationDetail> => {
  if (type !== 'teller') return LOCATION_DETAILS;
  const items = [...LOCATION_DETAILS.items];
  items.splice(
    items.findIndex(({ value }) => value === 'type') + 1,
    0,
    ...OTC_LOCATION_DETAILS_ITEMS,
  );
  return { ...LOCATION_DETAILS, items };
};

export const INSTALLED_MACHINES_COLUMNS: Columns<LocationInstalledMachines> = {
  items: [
    {
      hideSort: true,
      value: 'atm_id',
      label: 'Machine',
      valueMapper: (value: LocationInstalledMachines['atm_id']) =>
        value && (
          <ClickableText
            fixIcon
            text={value.toString()}
            clipboardValue={value.toString()}
            name="Machine ID"
            to={`/machine/${value}`}
          />
        ),
    },
    {
      hideSort: true,
      value: 'date_time_installed',
      label: 'Installed At',
      valueMapper: (value: LocationInstalledMachines['date_time_installed']) =>
        value ? new Date(value).toLocaleString() : '-',
    },
    {
      hideSort: true,
      value: 'date_time_uninstalled',
      label: 'Removed At',
      valueMapper: (value: LocationInstalledMachines['date_time_uninstalled']) =>
        value ? new Date(value).toLocaleString() : '-',
    },
  ],
};

export const INSTALLED_MACHINES_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'atm_id',
    label: 'Location',
    operator: 'equal',
    type: 'select',
  },
  {
    property: 'date_time_installed',
    label: 'Installed Date',
    type: 'select',
  },
];

/**
 * Location valid hours, this constant will be used to generate the schedule component
 */
export const LOCATION_VALID_HOURS = ['24-Hours', ...CONSTANTS.DAY_HOURS];

export const LOCATION_RANGES_PLACEHOLDERS = ['Opens at...', 'Closes at...'];

export const LOCATION_RANGES_FIRST = ['', ...LOCATION_VALID_HOURS];
export const LOCATION_RANGES_SECOND = ['', ...CONSTANTS.DAY_HOURS];

export const LOCATION_RANGES_RANGES_OPTIONS = [LOCATION_RANGES_FIRST, LOCATION_RANGES_SECOND];

export const LOCATION_SALES_STATS = {
  total_sales: {
    label: 'Total Sales',
    mapper: (value: number) => formatCurrency(value),
  },
  total_fees: {
    label: 'Total Fees',
    mapper: (value: number) => formatCurrency(value),
  },
  total_transactions: {
    label: 'Total transactions',
    mapper: (value: number) => value.toString(),
  },
};

const statMapper = (value: number) => (value !== undefined ? formatCurrency(value) : '-');
export const LOCATION_SALES_TRENDS: Columns<LocationSaleTrends> = {
  items: [
    {
      label: 'Today',
      value: 'day',
      valueMapper: statMapper,
    },
    {
      label: '7 days',
      value: 'week',
      valueMapper: statMapper,
    },
    {
      label: '30 days',
      value: 'month',
      valueMapper: statMapper,
    },
    {
      label: '90 days',
      value: 'quarter',
      valueMapper: statMapper,
    },
    {
      label: '365 days',
      value: 'year',
      valueMapper: statMapper,
    },
    {
      label: 'All',
      value: 'all',
      valueMapper: statMapper,
    },
  ],
};
